import React from 'react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { computeCognitiveLevelDistribution, cognitiveLevelLabel, cognitiveLevelColor, type CognitiveLevel } from '../../../governance/cognitiveLevel';
import type { Question } from '../../../types';
interface CognitiveLevelChartProps {
  questions: Question[];
}
const LEVEL_ORDER: CognitiveLevel[] = ['recall', 'comprehension', 'application', 'analysis'];
const LEVEL_DESCRIPTIONS: Record<CognitiveLevel, string> = {
  recall: 'Reproduce facts, definitions, procedures',
  comprehension: 'Explain meaning, interpret, paraphrase',
  application: 'Apply formula/rule to a new situation',
  analysis: 'Compare, contrast, decompose, reason'
};
const LEVEL_COLORS: Record<CognitiveLevel, {
  bar: string;
  badge: string;
  text: string;
}> = {
  recall: {
    bar: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-700',
    text: 'text-slate-600'
  },
  comprehension: {
    bar: 'bg-blue-400',
    badge: 'bg-blue-100 text-blue-700',
    text: 'text-blue-600'
  },
  application: {
    bar: 'bg-emerald-400',
    badge: 'bg-emerald-100 text-emerald-700',
    text: 'text-emerald-600'
  },
  analysis: {
    bar: 'bg-purple-400',
    badge: 'bg-purple-100 text-purple-700',
    text: 'text-purple-600'
  }
};
export function CognitiveLevelChart({
  questions
}: CognitiveLevelChartProps) {
  const distribution = computeCognitiveLevelDistribution(questions);
  const total = questions.length;
  // Target: ≥ 40% at Application or Analysis
  const higherOrderCount = distribution.application + distribution.analysis;
  const higherOrderPct = total > 0 ? Math.round(higherOrderCount / total * 100) : 0;
  const meetsTarget = higherOrderPct >= 40;
  return <Card className="p-5" data-id="element-2153">
      <div className="flex items-center justify-between mb-4" data-id="element-2154">
        <div data-id="element-2155">
          <h3 className="font-bold text-slate-900 text-sm" data-id="element-2156">
            Cognitive Level Distribution
          </h3>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-2157">
            Bloom's taxonomy (simplified 4-level)
          </p>
        </div>
        <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${meetsTarget ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-2158">
          {higherOrderPct}% higher-order
          {meetsTarget ? ' ✓' : ' (target: ≥40%)'}
        </div>
      </div>

      <div className="space-y-3" data-id="element-2159">
        {LEVEL_ORDER.map(level => {
        const count = distribution[level];
        const pct = total > 0 ? Math.round(count / total * 100) : 0;
        const colors = LEVEL_COLORS[level];
        return <div key={level} data-id="element-2160">
              <div className="flex items-center justify-between mb-1" data-id="element-2161">
                <div className="flex items-center gap-2" data-id="element-2162">
                  <span className={`text-xs font-bold ${colors.text}`} data-id="element-2163">
                    {cognitiveLevelLabel(level)}
                  </span>
                  <span className="text-xs text-slate-400" data-id="element-2164">
                    {LEVEL_DESCRIPTIONS[level]}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2165">
                  <span className="text-xs text-slate-500" data-id="element-2166">{count} q</span>
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${colors.badge}`} data-id="element-2167">
                    {pct}%
                  </span>
                </div>
              </div>
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2168">
                <div className={`h-full ${colors.bar} transition-all duration-500`} style={{
              width: `${pct}%`
            }} data-id="element-2169" />
              </div>
            </div>;
      })}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500" data-id="element-2170">
        <span data-id="element-2171">{total} questions analysed</span>
        <span className="text-slate-400" data-id="element-2172">
          Application + Analysis = {higherOrderCount} ({higherOrderPct}%)
        </span>
      </div>
    </Card>;
}